import React from "react";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Copy,
  ImagePlus,
  Plus,
  Save,
  X,
} from "lucide-react";
import ConfigurableJournalInput from "./ConfigurableJournalInput";
import { CHART_DROP_HIGHLIGHT_HALO_STYLE, CHART_DROP_HIGHLIGHT_STYLE } from "../utils/entryFormHooks";

const TICKERS = ["MES", "MNQ", "BTC", "GL", "SL", "MYM"];
const EMOTIONS = ["Calm", "Focused", "Confident", "Greedy", "Hesitant", "Frustrated", "Discouraged", "Tilted"];
const EXECUTION_OPTIONS = [
  "Perfectly executed",
  "Overtraded",
  "Over-risked",
  "Revenge traded",
  "Early exit",
  "Late exit",
  "Late entry",
  "Chased price",
  "Moved stop loss",
  "Impulsive trade",
  "Hesitated",
];

const panelClass = "theme-panel theme-border rounded-2xl border shadow-sm";
const inputClass = "theme-input theme-border theme-text w-full rounded-xl border px-3 py-2 text-sm outline-none transition focus:ring-2 focus:ring-[var(--theme-accent)]";
const labelClass = "theme-text-muted mb-1.5 block text-[10px] font-bold uppercase tracking-[0.16em]";
const environmentInputClass = "theme-input theme-border theme-text w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none transition focus:ring-2 focus:ring-[var(--theme-accent)]";
const environmentLabelClass = "theme-text-muted mb-1 flex min-h-[28px] items-end text-[9px] font-bold uppercase leading-[1.25] tracking-[0.13em]";

function Field({ label, children, className = "" }) {
  return (
    <label className={className}>
      <span className={labelClass}>{label}</span>
      {children}
    </label>
  );
}

function SectionHeading({ eyebrow, title }) {
  return (
    <div className="mb-2">
      {eyebrow && <div className="theme-accent text-[10px] font-bold uppercase tracking-[0.2em]">{eyebrow}</div>}
      <h2 className="theme-text text-lg font-bold">{title}</h2>
    </div>
  );
}

function GradePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-4 overflow-hidden rounded-xl border theme-border">
      {["A", "B", "C", "D"].map((grade) => (
        <button
          key={grade}
          type="button"
          onClick={() => onChange(value === grade ? "" : grade)}
          className={`h-[38px] border-l text-xs font-bold first:border-l-0 theme-border ${
            value === grade ? "theme-btn-primary" : "theme-panel-alt theme-text-muted theme-hover-surface"
          }`}
        >
          {grade}
        </button>
      ))}
    </div>
  );
}

function PnlPanel({ controller }) {
  const { form, handleChange, pnlToneColor } = controller;
  const hasDirectionalPnl = form.pnl !== "" && Number(form.pnl) !== 0;
  const directionalStyle = hasDirectionalPnl ? { color: pnlToneColor } : undefined;
  return (
    <div className="flex min-h-[82px] flex-col">
      <SectionHeading title="Net P&L" />
      <div
        className="theme-panel-alt theme-border flex min-h-[44px] flex-1 items-center overflow-hidden rounded-xl border px-3.5 py-1.5 transition-colors"
        style={hasDirectionalPnl ? { borderColor: pnlToneColor } : undefined}
      >
        <span className="theme-text-muted mr-2 shrink-0 text-xl font-black transition-colors" style={directionalStyle}>$</span>
        <input
          name="pnl"
          type="number"
          step="0.01"
          data-theme-input-mode="unstyled"
          value={form.pnl}
          onChange={handleChange}
          style={{ color: pnlToneColor, background: "transparent", WebkitTextFillColor: pnlToneColor }}
          className="unified-pnl-input min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-2xl font-black outline-none sm:text-3xl"
          placeholder="0.00"
          data-first-field
          autoFocus
        />
      </div>
    </div>
  );
}

function EssentialsPanel({ controller, variant = "split", showPnl = true }) {
  const {
    form,
    setForm,
    handleChange,
    logDate,
    handleMainEntryDateChange,
    pnlToneColor,
    tradeEnvFields,
    tradeEnv,
    setTradeEnv,
    mindsetExecutionBeta,
  } = controller;
  const compact = variant === "bento";
  const hasDirectionalPnl = form.pnl !== "" && Number(form.pnl) !== 0;
  const directionalStyle = hasDirectionalPnl ? { color: pnlToneColor } : undefined;
  const lossField = tradeEnvFields.find((field) => field.enabled && field.id === "lossClassification");
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`grid grid-cols-2 gap-x-2.5 gap-y-2 ${compact ? "h-full grid-rows-[auto_auto_auto_minmax(0,1fr)_auto]" : ""}`}>
        {showPnl && <Field label="Net P&L" className="col-span-2">
          <div
            className="theme-panel-alt theme-border flex items-center rounded-2xl border px-4 py-2.5 transition-colors"
            style={hasDirectionalPnl ? { borderColor: pnlToneColor } : undefined}
          >
            <span className="theme-text-muted mr-2 text-2xl font-bold transition-colors" style={directionalStyle}>$</span>
            <input
              name="pnl"
              type="number"
              step="0.01"
              value={form.pnl}
              onChange={handleChange}
              style={{ color: pnlToneColor }}
              className="min-w-0 flex-1 border-0 bg-transparent text-3xl font-black outline-none sm:text-4xl"
              placeholder="0.00"
              autoFocus
            />
          </div>
        </Field>}

        <Field label="Ticker">
          <input
            name="tickerTraded"
            value={form.tickerTraded}
            onChange={(event) => setForm((current) => ({ ...current, tickerTraded: event.target.value.toUpperCase() }))}
            list="unified-journal-tickers"
            className={`${inputClass} py-1.5`}
            placeholder="MNQ"
          />
          <datalist id="unified-journal-tickers">{TICKERS.map((ticker) => <option key={ticker} value={ticker} />)}</datalist>
        </Field>
        <Field label="R:R">
          <input name="rr" type="number" step="0.01" value={form.rr} onChange={handleChange} className={`${inputClass} py-1.5`} placeholder="2.5" />
        </Field>

        <Field label="Title" className="col-span-2">
          <input name="title" value={form.title} onChange={handleChange} className={`${inputClass} py-1.5`} placeholder="NQ long reversal" />
        </Field>

        <Field label="Date">
          <input type="date" value={logDate} onChange={handleMainEntryDateChange} className={`${inputClass} py-1.5`} />
        </Field>
        <Field label="Trade notes" className={`col-span-2 ${compact ? "flex min-h-0 flex-col" : ""}`}>
          <textarea
            name="notes"
            value={form.notes}
            onChange={handleChange}
            rows={compact ? 2 : 3}
            className={`${inputClass} resize-none py-1.5 ${compact ? "min-h-[58px] flex-1" : ""}`}
            placeholder="What happened and what mattered?"
          />
        </Field>

        {!mindsetExecutionBeta && lossField && (
          <div className="theme-panel-alt theme-border col-span-2 rounded-xl border p-2">
            <EnvironmentControl
              compact
              field={lossField}
              value={tradeEnv[lossField.id]}
              onChange={(value) => setTradeEnv((current) => ({ ...current, [lossField.id]: value }))}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function DetailsPanel({ controller, compact = false }) {
  const {
    form,
    setForm,
    handleChange,
    tradingStyle,
    setTradingStyle,
    swingEntryDate,
    handleSwingEntryDateChange,
    exitDate,
    setExitDate,
    handleExitDateChange,
    economicRelease,
    setEconomicRelease,
    tradeRating,
    setTradeRating,
    pdArray,
    setPdArray,
    target,
    setTarget,
    journalFields,
    rulesResponseForDate,
    handleRulesResponse,
    handleDurationChange,
    durationSuggestion,
    applySuggestedDuration,
  } = controller;
  const durationLabel = (
    <span className="flex items-center justify-between gap-1">
      Duration
      {durationSuggestion && (
        <button type="button" onClick={applySuggestedDuration} className="theme-accent normal-case tracking-normal hover:underline">
          Use {durationSuggestion}
        </button>
      )}
    </span>
  );
  const configurableFields = (journalFields || [])
    .filter((field) => ['tradeSession', 'economicRelease', 'pdArray', 'target', 'rulesFollowed'].includes(field.id))
    .filter((field) => field.enabled !== false);

  const renderConfigurableField = (field) => {
    if (field.id === 'rulesFollowed') {
      return (
        <div key={field.id} className="col-span-2">
          <span className={labelClass}>{field.label || "Follow rules?"}</span>
          <div className="theme-choice-group grid grid-cols-2 rounded-lg p-0.5">
            <button type="button" onClick={() => handleRulesResponse("yes")} className={`flex h-[38px] items-center justify-center gap-1.5 rounded-md text-xs font-bold ${rulesResponseForDate === "yes" ? "theme-choice-button-success-active" : "theme-choice-button-success"}`}><Check className="h-4 w-4" /> Yes</button>
            <button type="button" onClick={() => handleRulesResponse("no")} className={`flex h-[38px] items-center justify-center gap-1.5 rounded-md text-xs font-bold ${rulesResponseForDate === "no" ? "theme-choice-button-danger-active" : "theme-choice-button-danger"}`}><X className="h-4 w-4" /> No</button>
          </div>
        </div>
      );
    }

    const values = {
      tradeSession: form.tradeSession,
      economicRelease,
      pdArray,
      target,
    };
    const setters = {
      tradeSession: (value) => setForm((current) => ({ ...current, tradeSession: value })),
      economicRelease: setEconomicRelease,
      pdArray: setPdArray,
      target: setTarget,
    };
    return (
      <Field key={field.id} label={field.label} className={field.id === 'target' ? (compact ? "col-span-2" : "col-span-2 xl:col-span-1") : ""}>
        <ConfigurableJournalInput field={field} value={values[field.id]} onChange={setters[field.id]} className={inputClass} />
      </Field>
    );
  };

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <div className="theme-choice-group grid min-w-[174px] grid-cols-2 rounded-lg p-0.5">
          {["day", "swing"].map((style) => (
            <button
              key={style}
              type="button"
              onClick={() => {
                setTradingStyle(style);
                if (style === "day") setExitDate("");
              }}
              className={`rounded-md px-2 py-1.5 text-[10px] font-bold ${tradingStyle === style ? "theme-btn-primary" : "theme-text-muted theme-hover-surface"}`}
            >
              {style === "day" ? "Day Trading" : "Swing Trading"}
            </button>
          ))}
        </div>
      </div>
      <div className={`grid grid-cols-2 gap-3 ${compact ? "" : "xl:grid-cols-3"}`}>
        {tradingStyle === "swing" && (
          <>
            <Field label="Entry date"><input type="date" value={swingEntryDate} onChange={handleSwingEntryDateChange} className={inputClass} /></Field>
            <Field label="Exit date"><input type="date" value={exitDate} onChange={handleExitDateChange} className={inputClass} /></Field>
          </>
        )}
        <Field label="Entry time"><input name="entryTime" value={form.entryTime} onChange={handleChange} className={inputClass} placeholder="09:30" /></Field>
        <Field label="Exit time"><input name="exitTime" value={form.exitTime} onChange={handleChange} className={inputClass} placeholder="10:15" /></Field>
        <Field label={durationLabel}><input name="duration" value={form.duration} onChange={handleDurationChange} className={`${inputClass} theme-panel-alt`} placeholder="Auto" /></Field>

        <Field label="Trade grade"><GradePicker value={tradeRating} onChange={setTradeRating} /></Field>
        {configurableFields.map(renderConfigurableField)}
      </div>
    </div>
  );
}

function EnvironmentControl({ field, value, onChange, compact = false }) {
  const fieldType = field.fieldType || "yesNoNA";
  const controlLabelClass = compact
    ? "theme-text-muted mb-1 block text-[9px] font-bold uppercase tracking-[0.13em]"
    : environmentLabelClass;
  const controlInputClass = compact
    ? "theme-input theme-border theme-text w-full rounded-lg border px-2.5 py-1.5 text-xs outline-none transition focus:ring-2 focus:ring-[var(--theme-accent)]"
    : environmentInputClass;
  if (fieldType === "text") {
    return (
      <label className="min-w-0">
        <span className={controlLabelClass}>{field.label}</span>
        <input value={value || ""} onChange={(event) => onChange(event.target.value)} inputMode={field.inputMode || undefined} className={controlInputClass} placeholder={field.placeholder || "Enter value"} />
      </label>
    );
  }
  if (fieldType === "dropdown") {
    return (
      <label className="min-w-0">
        <span className={controlLabelClass}>{field.label}</span>
        <select value={value || ""} onChange={(event) => onChange(event.target.value)} className={controlInputClass}>
          <option value="">{field.placeholder || "Select"}</option>
          {(field.options || []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
    );
  }
  return (
    <div className="min-w-0">
      <span className={controlLabelClass}>{field.label}</span>
      <div className="theme-choice-group grid grid-cols-3 rounded-lg p-0.5">
        {[[true, "Yes"], [false, "No"], [null, "N/A"]].map(([optionValue, label]) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(optionValue)}
            className={`rounded-md px-1.5 ${compact ? "py-1 text-[9px]" : "py-1.5 text-[10px]"} font-bold ${value === optionValue ? "theme-btn-primary" : "theme-text-muted theme-hover-surface"}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function EnvironmentPanel({ controller }) {
  const { tradeEnvFields, tradeEnv, setTradeEnv } = controller;
  const fields = tradeEnvFields.filter((field) => field.enabled && field.id !== "lossClassification");
  return (
    <div>
      <div className="grid grid-cols-1 content-start gap-x-3 gap-y-2 md:grid-cols-2">
        {fields.map((field) => (
          <EnvironmentControl
            key={field.id}
            field={field}
            value={tradeEnv[field.id]}
            onChange={(value) => setTradeEnv((current) => ({ ...current, [field.id]: value }))}
          />
        ))}
      </div>
    </div>
  );
}

function ScreenshotPanel({ controller, prominent = false }) {
  const {
    screenshots,
    fileInputRef,
    handleFileChange,
    chartDropZoneProps,
    isDraggingChart,
    handlePasteImages,
    handleRemoveScreenshot,
    openScreenshot,
  } = controller;
  return (
    <div
      {...chartDropZoneProps}
      onPaste={handlePasteImages}
      data-dragging={isDraggingChart ? "true" : undefined}
      style={isDraggingChart ? (prominent ? CHART_DROP_HIGHLIGHT_HALO_STYLE : CHART_DROP_HIGHLIGHT_STYLE) : undefined}
      className={`flex h-full flex-col rounded-2xl transition-shadow ${prominent ? "min-h-[132px]" : "theme-panel-alt theme-border border border-dashed p-3"}`}
    >
      <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className={`theme-text font-bold ${prominent ? "text-lg" : "text-sm"}`}>{prominent ? "Add images" : "Charts"}</div>
        </div>
        <button type="button" onClick={() => fileInputRef.current?.click()} className="theme-btn-ghost flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold">
          <ImagePlus className="h-4 w-4" /> Add
        </button>
      </div>
      <input
        type="text"
        aria-label="Paste trade screenshots"
        value=""
        onChange={() => {}}
        onClick={(event) => event.stopPropagation()}
        onPaste={handlePasteImages}
        placeholder="Click here, then press Ctrl+V to paste images"
        className={`${inputClass} mt-2 py-1.5`}
      />
      {screenshots.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {screenshots.map((source, index) => (
            <div key={`${source.slice(0, 24)}-${index}`} className="relative shrink-0">
              <button type="button" onClick={() => openScreenshot(index)}><img src={source} alt={`Trade chart ${index + 1}`} className="h-20 w-28 rounded-lg object-cover" /></button>
              <button type="button" onClick={() => handleRemoveScreenshot(index)} aria-label={`Remove chart ${index + 1}`} className="absolute -right-1 -top-1 rounded-full bg-red-500 p-0.5 text-white"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      )}
      {screenshots.length === 0 && prominent && (
        <button type="button" onClick={() => fileInputRef.current?.click()} className="theme-panel-alt theme-text-muted mt-2 flex min-h-[42px] flex-1 items-center justify-center rounded-xl border border-dashed theme-border px-4 py-2 text-center text-xs font-semibold">
          Add entry and exit charts
        </button>
      )}
    </div>
  );
}

function ReviewPanel({ controller }) {
  const { tradeEnvFields, tradeEnv, setTradeEnv, mindsetExecutionBeta, mindsetExecution, setMindsetExecution } = controller;
  const lossField = tradeEnvFields.find((field) => field.enabled && field.id === "lossClassification");
  const selectedEmotions = mindsetExecution.emotions || [];
  return (
    <div className="h-full min-h-0">
      <div className="grid h-full min-h-0 content-evenly gap-3">
        {lossField && (
          <div className="theme-panel-alt theme-border rounded-xl border p-2">
            <EnvironmentControl compact field={lossField} value={tradeEnv[lossField.id]} onChange={(value) => setTradeEnv((current) => ({ ...current, [lossField.id]: value }))} />
          </div>
        )}

        {mindsetExecutionBeta && (
          <>
            <div className="grid gap-1.5 sm:grid-cols-[0.82fr_1fr_1.25fr]">
              <div className="relative min-w-0">
                <span className={labelClass}>Mindset</span>
                <details className="group relative">
                  <summary className="theme-input theme-border theme-text flex h-[32px] cursor-pointer list-none items-center justify-between gap-2 rounded-lg border px-2.5 text-xs font-semibold outline-none">
                    <span className="truncate">{selectedEmotions.length ? `${selectedEmotions.length} selected` : "Select"}</span>
                    <ChevronDown className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="theme-panel theme-border absolute left-0 top-full z-30 mt-1 grid w-full min-w-[180px] gap-0.5 rounded-lg border p-1.5 shadow-xl">
                    {EMOTIONS.map((emotion) => {
                      const active = selectedEmotions.includes(emotion);
                      return (
                        <button
                          key={emotion}
                          type="button"
                          onClick={() => setMindsetExecution((current) => ({ ...current, emotions: active ? current.emotions.filter((item) => item !== emotion) : [...current.emotions, emotion] }))}
                          className={`rounded-md px-2 py-1 text-left text-[10px] font-semibold ${active ? "theme-btn-primary" : "theme-text-muted theme-hover-surface"}`}
                        >
                          {emotion}
                        </button>
                      );
                    })}
                  </div>
                </details>
              </div>
              <Field label="Composure">
                <div className="grid grid-cols-5 overflow-hidden rounded-lg border theme-border">
                  {[1, 2, 3, 4, 5].map((score) => <button key={score} type="button" onClick={() => setMindsetExecution((current) => ({ ...current, composure: score }))} className={`h-[32px] border-l text-[10px] font-bold first:border-l-0 theme-border ${mindsetExecution.composure === score ? "theme-btn-primary" : "theme-panel-alt theme-text-muted"}`}>{score}</button>)}
                </div>
              </Field>
              <Field label="Execution">
                <select value={mindsetExecution.execution} onChange={(event) => setMindsetExecution((current) => ({ ...current, execution: event.target.value }))} className={`${environmentInputClass} h-[32px]`}>
                  <option value="">Select</option>
                  {EXECUTION_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid gap-1.5 md:grid-cols-3">
              {[["learned", "What did I learn?"], ["distraction", "What distracted me?"], ["improvement", "What could improve?"]].map(([key, label]) => (
                <label key={key} className="flex flex-col">
                  <span className={`${labelClass} flex min-h-[34px] items-end leading-tight`}>{label}</span>
                  <textarea rows={2} value={mindsetExecution[key]} onChange={(event) => setMindsetExecution((current) => ({ ...current, [key]: event.target.value }))} className={`${environmentInputClass} h-[72px] resize-none`} placeholder="Optional" />
                </label>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function UnifiedJournalPrototype({ controller }) {
  const canSave = controller.form.pnl !== "" && !controller.uploading && !controller.submitting;

  return (
    <form ref={controller.formRef} onSubmit={controller.handleSubmit} className="theme-app-shell fixed inset-0 z-[80] flex min-h-0 flex-col overflow-hidden">
      <style>{`
        .unified-pnl-input::-webkit-inner-spin-button,
        .unified-pnl-input::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .unified-pnl-input { -moz-appearance: textfield; }
      `}</style>
      <header className="theme-panel theme-border z-20 shrink-0 border-b px-3 py-2.5 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2.5">
            <button type="button" onClick={controller.onCancel} aria-label="Close unified journal" className="theme-btn-ghost rounded-xl p-2"><ArrowLeft className="h-5 w-5" /></button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="theme-text truncate text-base font-black sm:text-lg">Add Trade</h1>
                <span className="theme-accent-soft theme-accent hidden rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider sm:inline">Beta</span>
              </div>
              <div className="theme-text-muted truncate text-[11px]">{controller.accountName || "Trading account"} · Unified journal beta</div>
            </div>
          </div>

          {controller.onGoToQuick && controller.onGoToAdvanced && (
            <div role="group" aria-label="Journal mode" className="theme-choice-group hidden items-center gap-1 rounded-xl p-1 md:flex">
              <button type="button" onClick={() => controller.onGoToQuick('trade')} className="theme-text-muted theme-hover-surface rounded-lg px-3 py-2 text-xs font-semibold">Fast</button>
              <button type="button" onClick={controller.onGoToAdvanced} className="theme-text-muted theme-hover-surface rounded-lg px-3 py-2 text-xs font-semibold">Advanced</button>
              <button type="button" aria-pressed="true" className="theme-btn-primary pointer-events-none rounded-lg px-3 py-2 text-xs font-semibold">Beta</button>
            </div>
          )}

          <div className="flex items-center gap-1.5">
            {controller.onGoToQuick && <button type="button" onClick={() => controller.onGoToQuick("tape")} className="theme-btn-ghost hidden rounded-xl px-3 py-2 text-xs font-bold md:block">Tape Reading</button>}
            <button type="button" onClick={controller.handleCopyLastTrade} disabled={controller.copyingLastTrade} className="theme-btn-ghost flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold disabled:opacity-50"><Copy className="h-4 w-4" /><span className="hidden sm:inline">Copy Last</span></button>
            {controller.handleSaveAndAddAnother && (
              <button type="button" onClick={controller.handleSaveAndAddAnother} disabled={!canSave} aria-label="Save and add another" title="Save this trade and start a new one for the same day" className="theme-btn-ghost flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-40"><Plus className="h-4 w-4" /><span className="hidden sm:inline">Save &amp; add another</span></button>
            )}
            <button type="submit" disabled={!canSave} className="theme-btn-primary flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-black disabled:cursor-not-allowed disabled:opacity-40"><Save className="h-4 w-4" />{controller.uploading || controller.submitting ? "Saving" : "Save Trade"}</button>
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4 xl:overflow-hidden">
          <div className="grid min-h-full min-w-0 grid-cols-[minmax(0,1fr)] gap-3 xl:h-full xl:grid-cols-[minmax(0,0.88fr)_minmax(0,1.12fr)_minmax(0,0.9fr)]">
            <div className="grid min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] gap-2 xl:grid-rows-[auto_auto_minmax(0,1fr)]">
              <section className={`${panelClass} min-w-0 p-3`}><PnlPanel controller={controller} /></section>
              <section className={`${panelClass} min-w-0 p-3`}><ScreenshotPanel controller={controller} prominent /></section>
              <section className={`${panelClass} min-h-0 min-w-0 p-3.5`}><EssentialsPanel controller={controller} variant="bento" showPnl={false} /></section>
            </div>
            <section className={`${panelClass} min-w-0 overflow-y-auto p-3`}><EnvironmentPanel controller={controller} /></section>
            <div className={`grid min-h-0 min-w-0 grid-cols-[minmax(0,1fr)] gap-2.5 ${controller.mindsetExecutionBeta ? "xl:grid-rows-[auto_minmax(0,1fr)]" : "xl:grid-rows-[minmax(0,1fr)]"}`}>
              <section className={`${panelClass} min-h-0 min-w-0 p-3.5`}><DetailsPanel controller={controller} compact /></section>
              {controller.mindsetExecutionBeta && (
                <section className={`${panelClass} min-h-0 min-w-0 p-3.5`}><ReviewPanel controller={controller} /></section>
              )}
            </div>
          </div>
      </main>
    </form>
  );
}
